import axios from 'axios';
import { apolloClient } from '@/apollo';
import { IUpdateDocument } from '@/dealroom/document/types/update-document';
import { getVideoMock } from '@/dealroom/mocks/mock';
import { ERecipientRole } from '@/dealroom/recipients/types/enums/recipient-role';
import {
  addDocumentPageMutation,
  createRecipientMutation,
  deleteRecipientMutation,
  removeDocumentAttachmentMutation,
  removeDocumentPageMutation,
  updateDocumentAttachmentMutation,
  updateDocumentFromTemplateMutation,
  updateDocumentMutation,
  updateRecipientMutation,
  uploadFileMutation,
  sortDocumentPagesMutation,
  updateRecipientOrderMutation,
  updateDocumentPageMutation,
  sealDocumentMutation,
  updateSignatureMutation,
} from '@/graphql/mutations/dealroom.mutations';
import {
  documentAttachments,
  documentPageQuery,
  recipientsQuery,
} from '@/graphql/queries/dealroom.queries';
import { IDocument } from '@/types/Document';
import { IDocumentAttachment } from '@/types/document-attachment';
import { IDocumentPage } from '@/types/document-page';
import { IRecipient } from '@/types/Recipient';
import { IVideo } from '@/types/Video';
import { deleteDocumentsMutation } from '@/api/contract-management/graphql/contract-management.mutations';
import { applyTimezoneOffset } from '@/common/timezone/timezone.helper';
import { IUpdateDocumentPageInput } from '@/dealroom/types/update-document-page-input';

const updateDocumentPage = async (
  documentPage: IUpdateDocumentPageInput
): Promise<IDocumentPage> => {
  const {
    data: { updateDocumentPage },
  } = await apolloClient.mutate({
    mutation: updateDocumentPageMutation,
    variables: documentPage,
  });
  return updateDocumentPage;
};

const loadVideoPreview = async (videoUrl: string): Promise<IVideo> =>
  new Promise(res => res({ ...getVideoMock(videoUrl) } as IVideo));

const createVideoDocumentPage = async (
  documentId: string,
  videoId: string,
  pageNum: number
): Promise<IDocumentPage> => {
  const {
    data: { addDocumentPage },
  } = await apolloClient.mutate({
    mutation: addDocumentPageMutation,
    variables: { documentId, videoId, pageNum, pageType: 'video' },
  });
  return addDocumentPage;
};

const createEditorBlock = async (documentId: string, pageNum: number): Promise<IDocumentPage> => {
  const {
    data: { addDocumentPage },
  } = await apolloClient.mutate({
    mutation: addDocumentPageMutation,
    variables: { documentId, pageNum, pageType: 'editorBlock' },
  });
  return addDocumentPage;
};

const deleteDocumentPage = async (documentPageId: string): Promise<boolean> => {
  const { data } = await apolloClient.mutate({
    mutation: removeDocumentPageMutation,
    variables: { id: documentPageId },
  });
  return data;
};

const uploadFile = async (documentId: string, index: number, file: File): Promise<boolean> => {
  const { data } = await apolloClient.mutate({
    mutation: uploadFileMutation,
    variables: { documentId, index, file },
  });
  return data;
};

const loadDocumentPages = async (documentId: string): Promise<IDocumentPage[]> => {
  const {
    data: { document },
  } = await apolloClient.query({
    query: documentPageQuery,
    variables: { documentId },
    fetchPolicy: 'network-only',
  });
  return document.pages;
};

const updateDocument = async (payload: IUpdateDocument, documentId: string): Promise<string> => {
  const {
    data: { updateDocument },
  } = await apolloClient.mutate({
    mutation: updateDocumentMutation,
    variables: { ...correctDatesUpdate(payload), documentId },
  });
  return updateDocument.id;
};

const createRecipient = async (
  contactId: string,
  documentId: string,
  role: ERecipientRole
): Promise<IRecipient> => {
  const {
    data: { createRecipient },
  } = await apolloClient.mutate({
    mutation: createRecipientMutation,
    variables: { documentId, contactId, role },
  });
  return createRecipient;
};

const deleteRecipient = async (id: string): Promise<boolean> => {
  const { data } = await apolloClient.mutate({
    mutation: deleteRecipientMutation,
    variables: { id },
  });
  return !!data;
};

const updateRecipient = async (id: string, properties: Partial<IRecipient>) => {
  const { data } = await apolloClient.mutate({
    mutation: updateRecipientMutation,
    variables: { id, ...properties },
  });
  return data;
};

const attachmentRequireView = async (documentAttachment: IDocumentAttachment): Promise<boolean> => {
  const { data } = await apolloClient.mutate({
    mutation: updateDocumentAttachmentMutation,
    variables: {
      id: documentAttachment.id,
      requireView: documentAttachment.requireView,
    },
  });
  return !!data;
};

const attachmentRestrictAccess = async (
  documentAttachment: IDocumentAttachment,
  recipientIds: string[]
): Promise<boolean> => {
  const { data } = await apolloClient.mutate({
    mutation: updateDocumentAttachmentMutation,
    variables: {
      id: documentAttachment.id,
      accessRecipientList: recipientIds,
    },
  });
  return !!data;
};

const attachmentRemoveRestrictedAccess = async (
  documentAttachment: IDocumentAttachment
): Promise<boolean> => {
  const { data } = await apolloClient.mutate({
    mutation: updateDocumentAttachmentMutation,
    variables: {
      id: documentAttachment.id,
      accessRecipientList: null,
    },
  });
  return !!data;
};

const deleteAttachment = async (documentAttachment: IDocumentAttachment): Promise<boolean> => {
  const { data } = await apolloClient.mutate({
    mutation: removeDocumentAttachmentMutation,
    variables: {
      id: documentAttachment.id,
    },
  });
  return data;
};

const loadDocumentAttachments = async (documentId: string): Promise<IDocumentAttachment[]> => {
  const {
    data: { document },
  } = await apolloClient.query({
    query: documentAttachments,
    variables: { documentId },
    fetchPolicy: 'network-only',
  });

  return document.documentAttachments;
};

const loadRecipients = async (documentId: string): Promise<IRecipient[]> => {
  const {
    data: { document },
  } = await apolloClient.query({
    query: recipientsQuery,
    variables: { documentId },
    fetchPolicy: 'network-only',
  });
  return document.recipients;
};

const useTemplate = async (
  documentId: string,
  templateId: string,
  pageOffset: number
): Promise<IDocument> => {
  const {
    data: { updateDocumentFromTemplate },
  } = await apolloClient.mutate({
    mutation: updateDocumentFromTemplateMutation,
    variables: {
      documentId,
      templateId,
      pageOffset,
    },
  });
  return updateDocumentFromTemplate;
};

const deleteDocuments = async (ids: [string]): Promise<IDocument> => {
  const { data } = await apolloClient.mutate({
    mutation: deleteDocumentsMutation,
    variables: {
      ids,
    },
  });
  return data;
};

const sortDocumentPages = async (
  documentId: string,
  documentPageIds: string[],
  pageNums: number[]
): Promise<IDocumentPage[]> => {
  const {
    data: { sortDocumentPages },
  } = await apolloClient.mutate({
    mutation: sortDocumentPagesMutation,
    variables: {
      documentId,
      documentPageIds,
      pageNums,
    },
  });
  return sortDocumentPages;
};

const updateRecipientOrder = async (documentId: string, recipientOrder: Partial<IRecipient>[]) => {
  const { data } = await apolloClient.mutate({
    mutation: updateRecipientOrderMutation,
    variables: {
      documentId,
      recipientOrder,
    },
  });
  return data;
};

export function correctDates(document: IDocument): IDocument {
  if (!document) {
    return null;
  }
  return {
    ...document,
    expirationDate: applyTimezoneOffset(document.expirationDate, true),
    contractEndDate: applyTimezoneOffset(document.contractEndDate, true),
    contractStartDate: applyTimezoneOffset(document.contractStartDate, true),
  };
}

export function correctDatesUpdate(payload: IUpdateDocument): IUpdateDocument {
  const res: IUpdateDocument = payload;
  if (res.contractStartDate) {
    res.contractStartDate = applyTimezoneOffset(payload.contractStartDate, false);
  }
  if (res.contractEndDate) {
    res.contractEndDate = applyTimezoneOffset(payload.contractEndDate, false);
  }
  if (res.expirationDate) {
    res.expirationDate = applyTimezoneOffset(payload.expirationDate, false);
  }
  return res;
}

const createDocument = async (body: any): Promise<string> => {
  const {
    data: { document_id },
  } = await axios.post('/save/document', body);
  return document_id;
};

const sealDocument = async (documentId: string, sendDocument: boolean): Promise<void> => {
  await apolloClient.mutate({
    mutation: sealDocumentMutation,
    variables: {
      documentId,
      sendDocument,
    },
  });
};

export async function deleteTemplate(id: string): Promise<void> {
  const body: FormData = new FormData();
  body.append('action', 'delete');
  body.append('document-id', id);
  await axios.post('/save/board', body);
}

const updateSignature = async (signature: string): Promise<void> => {
  await apolloClient.mutate({
    mutation: updateSignatureMutation,
    variables: {
      signature,
    },
  });
};

export async function assignFieldToRecipient(
  documentId: string,
  recipientId: string
): Promise<void> {
  const body: FormData = new FormData();
  body.append('action', 'assign-fields');
  body.append('document-id', documentId);
  body.append('recipient-id', recipientId);
  await axios.post('/save/document', body);
}

export default {
  createVideoDocumentPage,
  createEditorBlock,
  createRecipient,
  deleteDocumentPage,
  loadDocumentPages,
  loadVideoPreview,
  loadRecipients,
  updateDocumentPage,
  uploadFile,
  updateDocument,
  deleteRecipient,
  updateRecipient,
  attachmentRequireView,
  deleteAttachment,
  attachmentRestrictAccess,
  attachmentRemoveRestrictedAccess,
  loadDocumentAttachments,
  useTemplate,
  deleteDocuments,
  sortDocumentPages,
  updateRecipientOrder,
  createDocument,
  sealDocument,
  deleteTemplate,
  updateSignature,
  assignFieldToRecipient,
};
